"use client";

import { useEffect, useRef, type RefObject } from "react";

/*
 * Gemeinsames Verhalten der Modal-Hüllen (Musterkatalog M-008, Design-Guide `detail-formen.md`):
 *  - Esc schließt (außer solange der Vorgang läuft: `gesperrt`),
 *  - Fokusfalle: Tab/Umschalt+Tab bleiben im Dialog,
 *  - der Fokus kehrt zum Auslöser zurück, wenn das Modal schließt,
 *  - der Hintergrund scrollt nicht (Klasse `overflow-hidden` am `body`, kein `style=`).
 * Mehrere offene Modale (Stapel): nur das oberste reagiert auf Esc und Tab (D6b „kein Dialog auf Dialog“ bleibt Regel,
 * der Stapel schützt trotzdem vor doppeltem Schließen).
 * Die Hülle verwendet kein `autoFocus`: den Anfangsfokus setzt dieser Hook (Feld mit `data-modal-fokus`, sonst das erste
 * Eingabefeld, sonst das erste bedienbare Element, sonst der Dialog selbst), damit der Auslöser vorher feststeht.
 */

// Selektoren als Einzel-Zeichenketten, die der Klassenwächter nicht für Tailwind-Klassenlisten hält.
const EINGABEFELDER = ['input:not([type="hidden"]):not([disabled])', "textarea:not([disabled])", "select:not([disabled])"];
const FOKUSSIERBAR = ["a[href]", "button:not([disabled])", ...EINGABEFELDER, '[tabindex]:not([tabindex="-1"])'].join(",");

/** Bedienbare Elemente im Dialog in Dokumentreihenfolge (verborgene und `aria-hidden` ausgenommen). */
export function fokussierbareElemente(huelle: HTMLElement): HTMLElement[] {
  return Array.from(huelle.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter(
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true",
  );
}

// Offenes, das Esc vor dem Modal bekommt: Menü-Fläche (`role="menu"`) und Felder mit offener Liste.
const ESC_VORRANG = '[role="menu"],[data-esc-eigen="offen"]';

const stapel: symbol[] = [];

/** Anzahl der gerade offenen Modale (für die Lage eines zweiten Modals über einem ersten, A6 P3 U2). */
export function stapelTiefe(): number {
  return stapel.length;
}

export function useModalVerhalten({
  offen,
  huelle,
  beimSchliessen,
  gesperrt = false,
  beiStapelPlatz,
}: {
  /**
   * Meldet nach dem Eintragen in den Stapel die Tiefe unter diesem Modal (0 = erstes Modal). Beim Wechsel von einem Modal zu
   * einem anderen (Anlegen → Serie) liest `stapelTiefe()` beim Rendern das schließende Modal noch mit; erst hier, nach dessen
   * Aufräumen, stimmt die Tiefe (U3b).
   */
  beiStapelPlatz?: (tiefe: number) => void;
  offen: boolean;
  huelle: RefObject<HTMLElement | null>;
  beimSchliessen: () => void;
  /** Solange der Vorgang läuft, schließt Esc nicht (die Eingaben bleiben, r98). */
  gesperrt?: boolean;
}) {
  const schliessenRef = useRef(beimSchliessen);
  const gesperrtRef = useRef(gesperrt);
  const platzRef = useRef(beiStapelPlatz);
  useEffect(() => {
    schliessenRef.current = beimSchliessen;
    gesperrtRef.current = gesperrt;
    platzRef.current = beiStapelPlatz;
  }, [beimSchliessen, gesperrt, beiStapelPlatz]);

  useEffect(() => {
    if (!offen) return;
    const el = huelle.current;
    if (!el) return;
    const ich = Symbol("modal");
    stapel.push(ich);
    platzRef.current?.(stapel.length - 1);
    const ausloeser = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.classList.add("overflow-hidden");

    const ziel =
      el.querySelector<HTMLElement>("[data-modal-fokus]") ??
      el.querySelector<HTMLElement>(EINGABEFELDER.join(",")) ??
      fokussierbareElemente(el)[0] ??
      el;
    ziel.focus();

    const beiTaste = (e: KeyboardEvent) => {
      if (stapel[stapel.length - 1] !== ich) return;
      if (e.key === "Escape") {
        // Esc-Vorrang für Offenes im Dialog (Aufgaben-Detail M-011): steht der Fokus in einem geöffneten Menü oder in
        // einem Feld mit offener Liste (`data-esc-eigen`, z. B. @-Liste des Composers), schließt Esc zuerst DIES; das
        // Modal bleibt. Das Offene fängt die Taste selbst (Dropdown-Menü, Composer).
        const aktiv = document.activeElement;
        if (aktiv instanceof HTMLElement && aktiv.closest(ESC_VORRANG)) return;
        e.preventDefault();
        e.stopPropagation();
        if (!gesperrtRef.current) schliessenRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const liste = fokussierbareElemente(el);
      if (liste.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const erstes = liste[0]!;
      const letztes = liste[liste.length - 1]!;
      const aktiv = document.activeElement;
      // Menü-Fläche im Portal (Dropdown am Dokumentende, über dem Modal): Tab gehört dem Menü, die Falle greift nicht.
      if (aktiv instanceof HTMLElement && aktiv.closest('[role="menu"]')) return;
      if (!aktiv || !el.contains(aktiv)) {
        e.preventDefault();
        erstes.focus();
      } else if (e.shiftKey && (aktiv === erstes || aktiv === el)) {
        e.preventDefault();
        letztes.focus();
      } else if (!e.shiftKey && aktiv === letztes) {
        e.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("keydown", beiTaste, true);

    return () => {
      document.removeEventListener("keydown", beiTaste, true);
      const i = stapel.indexOf(ich);
      if (i >= 0) stapel.splice(i, 1);
      if (stapel.length === 0) document.body.classList.remove("overflow-hidden");
      if (ausloeser && document.contains(ausloeser)) ausloeser.focus();
    };
  }, [offen, huelle]);
}
